import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import i18next from 'i18next';

import { AgoraAPI } from '../api/agoraApi';
import { isAgoraPermissionError, isAlreadyJoinError, isLockError } from '../api/apiError';
import { ChatGroupRoomApi } from '../api/chatGroupRoomApi';
import {
    ChatGroupRoomUserApi,
    ChatGroupRoomUserResponse,
    V1ChatGroupRoomDetailResponse,
} from '../api/chatGroupRoomUserApi';
import { GetDefaultMic, Mic, Voice } from '../model/Voice';
import { ChatGroup } from '../model/chatGroup';
import { ChatGroupRoom } from '../model/chatGroupRoom';
import { Member } from '../model/member';
import { RootState } from '../rootReducer';
import { addUnloadEventListener, clearUnloadEventListener } from '../utils/eventListener';
import { setPopupError, withTopError } from './errorSlice';
import { ThunkConfig } from './thunkConfig';

export const initAgoraHandler = createAsyncThunk<void, void, ThunkConfig>(
    'voiceCall/initAgoraHandler',
    async (_, thunkAPI) => {
        AgoraAPI.initAgoraHandler(thunkAPI.dispatch);
    }
);

export const getUserMics = createAsyncThunk<Mic[], void, ThunkConfig>('voiceCall/getUserMics', async (_, thunkAPI) => {
    try {
        const mics = await AgoraAPI.getUserMics();
        return mics.map((mic) => ({
            deviceId: mic.deviceId,
            label: mic.label,
        }));
    } catch (e) {
        thunkAPI.dispatch(setPopupError(i18next.t('error.inputDeviceNotFound')));
        throw e;
    }
});

export const joinRoom = createAsyncThunk<
    { group: ChatGroup; room: ChatGroupRoom; members: ChatGroupRoomUserResponse[] },
    { group: ChatGroup; room: ChatGroupRoom; userId: number },
    ThunkConfig
>(`voiceCall/joinRoom`, async (params, thunkAPI) => {
    const dispatch = thunkAPI.dispatch;
    const state = thunkAPI.getState().voiceCall;
    if (state.currentMic === null) {
        dispatch(setPopupError(i18next.t('error.inputDeviceNotFound')));
        throw new Error('currentMic is not found');
    }

    let chatGroupRoomDetail: V1ChatGroupRoomDetailResponse;
    try {
        chatGroupRoomDetail = await ChatGroupRoomUserApi(params.group.id, params.room.id).create();
    } catch (e) {
        if (isAlreadyJoinError(e)) {
            dispatch(setPopupError(i18next.t('error.alreadyJoin')));
        } else if (isLockError(e)) {
            dispatch(setPopupError(i18next.t('error.roomLocked')));
        } else {
            dispatch(setPopupError(i18next.t('error.cantJoin')));
        }
        throw e;
    }

    try {
        // Agoraで入室前にaddUnloadEventListenerを呼ばないと、beforeunloadが解除されない
        addUnloadEventListener(params.group.id, params.room.id);
        await AgoraAPI.startBasicCall(
            params.room.id.toString(),
            chatGroupRoomDetail.token,
            params.userId,
            state.currentMic
        );
        return { group: params.group, room: params.room, members: chatGroupRoomDetail.users };
    } catch (e) {
        // reset chat_group_room_users
        await ChatGroupRoomUserApi(params.group.id, params.room.id).leave();
        if (isAgoraPermissionError(e)) {
            dispatch(setPopupError(i18next.t('error.micePermission')));
        } else {
            dispatch(setPopupError(i18next.t('error.cantJoin')));
        }
        throw e;
    }
});

export const updateMembers = createAsyncThunk<ChatGroupRoomUserResponse[], void, ThunkConfig>(
    'voiceCall/getMembers',
    withTopError(async (_: void, { getState }) => {
        const state = getState().voiceCall;
        const groupId = state.group?.id;
        const roomId = state.room?.id;
        if (groupId !== undefined && roomId !== undefined) {
            const room = await ChatGroupRoomUserApi(groupId, roomId).index();
            return room.users;
        }
        return [];
    }, i18next.t('error.internalServerError'))
);

export const leaveRoom = createAsyncThunk(
    `voiceCall/leaveRoom`,
    withTopError(async (params: { groupId: number; roomId: number }) => {
        await AgoraAPI.leaveCall();
        await ChatGroupRoomUserApi(params.groupId, params.roomId).leave();
        clearUnloadEventListener();
    }, i18next.t('error.internalServerError'))
);

export const setMute = createAsyncThunk<boolean, { groupId: number; roomId: number; isMute: boolean }, ThunkConfig>(
    'voiceCall/setMute',
    withTopError(async (params, { getState }) => {
        const state = getState().voiceCall;
        await AgoraAPI.setMute(params.isMute, state.myVoice.inputVolume);
        await ChatGroupRoomUserApi(params.groupId, params.roomId).update(params.isMute);
        return params.isMute;
    }, i18next.t('error.mute'))
);

export const setInputVolume = createAsyncThunk<number, number, ThunkConfig>(
    'voiceCall/setInputVolume',
    async (volume, thunkAPI) => {
        const isMute = thunkAPI.getState().voiceCall.myVoice.isMute;
        await AgoraAPI.setMyInputVolume(isMute, volume);
        return volume;
    }
);

export const setOutputVolumeOfMember = createAsyncThunk<
    { memberId: number; volume: number },
    { memberId: number; volume: number },
    ThunkConfig
>('voiceCall/setOutputVolumeOfMember', async ({ memberId, volume }, { getState }) => {
    const state = getState().voiceCall;
    const member = state.members.find((member) => member.id === memberId);
    if (member === undefined) {
        throw new Error('member is not found');
    }

    await AgoraAPI.setMemberOutputVolume(member.id, volume);
    return {
        memberId: memberId,
        volume: volume,
    };
});

export const lockRoom = createAsyncThunk<boolean, { groupId: number; roomId: number; isLock: boolean }, ThunkConfig>(
    `voiceCall/lockRoom`,
    withTopError(async (params) => {
        const room = await ChatGroupRoomApi(params.groupId).lock(params.roomId, params.isLock);
        return room.is_lock;
    }, i18next.t('error.lock'))
);

export const setMic = createAsyncThunk<string, string>('voiceCall/setMic', async (deviceId) => {
    await AgoraAPI.setMic(deviceId);
    return deviceId;
});

export const activateCurrentMic = createAsyncThunk<
    { currentMic: Mic | null; mics: Mic[] },
    { deviceId: string; mics: Mic[] }
>('voiceCall/activateCurrentMic', async (params) => {
    await AgoraAPI.setMic(params.deviceId);
    const newMic = params.mics.find((mic) => mic.deviceId === params.deviceId);
    return {
        mics: params.mics,
        currentMic: newMic ? newMic : null,
    };
});

export const deactivateCurrentMic = createAsyncThunk<
    { currentMic: Mic | null; mics: Mic[] },
    { deviceId: string; mics: Mic[] },
    ThunkConfig
>('voiceCall/deactivateCurrentMic', async (params, thunkAPI) => {
    const state = thunkAPI.getState().voiceCall;
    let currentDeviceId = state.currentMic?.deviceId;
    // Chrome用の対応
    if (currentDeviceId === 'default') {
        const oldMics = state.mics;
        currentDeviceId = GetDefaultMic(oldMics)?.deviceId;
    }

    if (currentDeviceId === params.deviceId) {
        // 現在使用しているdeviceが変更された時
        const newMic = params.mics[0];
        await AgoraAPI.setMic(newMic.deviceId);
        return { currentMic: newMic, mics: params.mics };
    }
    return { currentMic: state.currentMic, mics: params.mics };
});

export enum NetworkQuality {
    Unknown,
    Good,
    Normal,
    Bad,
    Offline,
}

export type VoiceCallState = {
    // 通話中かどうか
    onCall: boolean;
    // 参加したグループ
    group: ChatGroup | undefined;
    // 参加したルーム
    room: ChatGroupRoom | undefined;
    // 自分の音声情報
    myVoice: Voice;
    // memberとその音声情報をもつ配列
    members: (Member & Voice)[];
    networkQuality: NetworkQuality;
    mics: Mic[];
    currentMic: Mic | null;
};

const initialState: VoiceCallState = {
    onCall: false,
    group: undefined,
    room: undefined,
    myVoice: {
        isMute: false,
        inputVolume: 100,
        outputVolume: 100,
        isTalking: false,
    },
    members: [],
    networkQuality: NetworkQuality.Unknown,
    mics: [],
    currentMic: null,
};

export const voiceCallSlice = createSlice({
    name: 'voiceCall',
    initialState,
    reducers: {
        setIsTalking: (state, { payload }: PayloadAction<{ userId: number; volume: number }>) => {
            const member = state.members.find((member) => member.id === payload.userId);
            if (member) {
                member.isTalking = payload.volume > 0;
            }
        },
        updateRoom: (state, { payload }: PayloadAction<ChatGroupRoom>) => {
            state.room = payload;
        },
        updateNetworkQuality: (state, { payload }: PayloadAction<NetworkQuality>) => {
            state.networkQuality = payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getUserMics.fulfilled, (state, action) => {
            state.mics = action.payload;
            state.currentMic = state.mics[0];
        });
        builder.addCase(joinRoom.fulfilled, (state, action) => {
            state.onCall = true;
            state.group = action.payload.group;
            state.room = action.payload.room;
            state.members = action.payload.members.map((member) => ({
                id: member.id,
                name: member.name,
                icon: member.user_image,
                isMute: member.is_muted,
                inputVolume: 100,
                outputVolume: 100,
                isTalking: false,
            }));
            // reset myVoice https://github.com/ReactCorp/Cocalero_mvp/issues/1112
            state.myVoice = {
                isMute: false,
                inputVolume: 100,
                outputVolume: 100,
                isTalking: false,
            };
        });
        builder.addCase(leaveRoom.fulfilled, (state) => {
            state.onCall = false;
            state.group = undefined;
            state.room = undefined;
            state.networkQuality = NetworkQuality.Unknown;
        });
        builder.addCase(setMute.fulfilled, (state, action) => {
            state.myVoice.isMute = action.payload;
        });
        builder.addCase(setInputVolume.fulfilled, (state, action) => {
            state.myVoice.inputVolume = action.payload;
        });
        builder.addCase(setOutputVolumeOfMember.fulfilled, (state, action) => {
            const idx = state.members.findIndex((member) => member.id === action.payload.memberId);
            if (state.members[idx]) {
                state.members[idx].outputVolume = action.payload.volume;
            }
        });
        builder.addCase(updateMembers.fulfilled, (state, action) => {
            const beforeMembers = state.members;
            state.members = action.payload.map((newMember) => {
                const joinedMember = beforeMembers.find((m) => m.id === newMember.id);
                if (joinedMember !== undefined) {
                    return {
                        ...joinedMember,
                        isMute: newMember.is_muted,
                    };
                }
                return {
                    id: newMember.id,
                    name: newMember.name,
                    icon: newMember.user_image,
                    isMute: newMember.is_muted,
                    inputVolume: 100,
                    outputVolume: 100,
                    isTalking: false,
                };
            });
        });
        builder.addCase(lockRoom.fulfilled, (state, action) => {
            if (state.room !== undefined) {
                state.room.isLock = action.payload;
            }
        });
        builder.addCase(setMic.fulfilled, (state, action) => {
            const mic = state.mics.find((mic) => mic.deviceId === action.payload);
            if (mic !== undefined) {
                state.currentMic = mic;
            }
        });
        builder.addCase(activateCurrentMic.fulfilled, (state, action) => {
            state.currentMic = action.payload.currentMic;
            state.mics = action.payload.mics;
        });
        builder.addCase(deactivateCurrentMic.fulfilled, (state, action) => {
            state.currentMic = action.payload.currentMic;
            state.mics = action.payload.mics;
        });
    },
});

export const { updateRoom, setIsTalking, updateNetworkQuality } = voiceCallSlice.actions;

export const selectVoiceCall = (state: RootState): VoiceCallState => state.voiceCall;

export const voiceCallReducer = voiceCallSlice.reducer;
